import httpClient, { authorizeClient, httpClientCoreQuery } from '@/api/http-client';
import { CoreQuery } from '@/common/services/core-query';
import { EntitiesList } from '@/common/models/entities.enum';
import { PaginatedDataUnmapped } from '@/common/models/paginated-data';
import { CoreEntitiesFilter } from '@/common/classes';
import { BuilderCourse, CoursePage } from '@/admin/courses/store/interfaces';
import { FluentFilterCollection, Operator } from '@/common/services';
import { IUnmappedAdminCourse } from '@/admin/courses/models/IAdminCourse';
import { debounce } from 'lodash-es';
import {
    AdminOverviewApiFactory,
    BuilderChapterApiFactory,
    BuilderCourseApiFactory,
    BuilderPageApiFactory,
    CourseType,
    PlayerQuizApiFactory,
} from '@eloomi/eloomi-learning-persona-client/1.0';

import { AttachmentContentElement, VideoContentElement } from '@/admin/courses/utils';
import { Filter } from '@/reporting/models/segment';
import { extractAxiosData } from '@/common/services/extractAxiosData';
import { UpdateCourseRequest, UpdatePageRequest } from '@eloomi/eloomi-learning-persona-client/1.0/models';
import {
    BuilderContentApiFactory,
    PutQuizOpenTextRequest,
    QuizApiFactory,
    PutQuizRatingScaleRequest,
    QuizChoiceQuestionContentBuilderContract,
    StoreQuizAttemptRequest,
} from '@eloomi/eloomi-content-persona-client/1.0';
import {
    ChoiceQuestionContentBuilderContract,
    PutIframeContentRequest,
} from '@eloomi/eloomi-content-persona-client/1.0/models';
import { Filters, FilterType } from '@/common/components/filter';
import { DataRequestOptions } from '@/common/components/table';

/* eslint-disable @typescript-eslint/no-explicit-any */
const adminOverviewApiClient: any = authorizeClient(AdminOverviewApiFactory);
const builderCourseApiClient: any = authorizeClient(BuilderCourseApiFactory);
const builderChapterApiClient: any = authorizeClient(BuilderChapterApiFactory);
const builderPageApiClient: any = authorizeClient(BuilderPageApiFactory);
const builderContentApiClient: any = authorizeClient(BuilderContentApiFactory);
const quizApiClient = authorizeClient(QuizApiFactory);
const playerQuizApiClient = authorizeClient(PlayerQuizApiFactory);
/* eslint-enable @typescript-eslint/no-explicit-any */

interface GetAllCoursesOptions {
    courseNameSearchQuery?: string;
    page?: number;
    pageSize?: number;
    filter?: Filter;
}

export interface CourseListRequestFilters extends Filters {
    courseTitle?: {
        value: string;
    };

    courseId?: {
        value: Array<number>;
        mode: 'inclusive' | 'exclusive';
    };
}

export type CourseListRequestOptions = DataRequestOptions<CourseListRequestFilters>;

export function getCourse(id: number): Promise<BuilderCourse> {
    return builderCourseApiClient
        .getCourseWithStructure(id)
        .then(extractAxiosData)
        .then(data => data as BuilderCourse);
}

export function editCourse(id: number, course: UpdateCourseRequest) {
    return builderCourseApiClient.editCourse(id, course).then(extractAxiosData);
}

export function createChapter(id, chapter) {
    return builderChapterApiClient.addChapter(id, chapter).then(extractAxiosData);
}

export function createPage(courseId, chapterId, page): Promise<CoursePage> {
    return builderPageApiClient
        .createPage(courseId, chapterId, page)
        .then(extractAxiosData)
        .then(data => data as CoursePage);
}

export function updatePage({
    courseId,
    chapterId,
    pageId,
    page,
}: {
    courseId: number;
    chapterId: number;
    pageId: number;
    page: CoursePage;
}) {
    return builderPageApiClient
        .updatePage(courseId, chapterId, pageId, page as UpdatePageRequest)
        .then(extractAxiosData);
}

export function getPageWithContent({ courseId, chapterId, pageId }) {
    return builderPageApiClient
        .getPage(courseId, chapterId, pageId)
        .then(extractAxiosData)
        .then(data => data as CoursePage);
}

export function deletePage({ courseId, chapterId, pageId }) {
    return builderPageApiClient.deletePage(courseId, chapterId, pageId);
}

export function getAllCourses(options: GetAllCoursesOptions = {}) {
    const filters = new FluentFilterCollection();

    options.courseNameSearchQuery !== undefined
        ? filters.where('title').filter(Operator.Contains, options.courseNameSearchQuery)
        : undefined;

    const pageNumber = options.page ?? 1;
    const pageSize = options.pageSize ?? 10;

    if (options.filter?.mode) {
        if (options.filter.mode === 'inclusive') {
            filters.where('id').filter(Operator.IsEqualTo, options.filter?.selectedIds.join('|'));
        } else {
            options.filter.selectedIds.forEach(id => {
                filters.where('id').filter(Operator.IsNotEqualTo, id);
            });
        }
    }

    if (options.filter?.doNotShowArchived) {
        filters.where('archived_at').filter(Operator.IsEqualTo, 'null');
    }

    const q = new CoreQuery()
        .path([EntitiesList.LEARNING, EntitiesList.PERSONA, EntitiesList.OVERVIEW])
        .filters(filters)
        .param({
            page: pageNumber,
            page_size: pageSize,
        });

    return httpClientCoreQuery.get<PaginatedDataUnmapped<IUnmappedAdminCourse>>(q).then(extractAxiosData);
}

function getCourseListFiltersParam(options: CourseListRequestFilters) {
    const filters = new FluentFilterCollection();

    if (options.courseTitle && options.courseTitle.value) {
        filters.where('title').filter(Operator.Contains, options.courseTitle.value);
    }

    if (options.courseId && options.courseId.value.length > 0) {
        if (options.courseId.mode === 'inclusive') {
            filters.where('id').filter(Operator.IsEqualTo, options.courseId.value.join('|'));
        }

        if (options.courseId.mode === 'exclusive') {
            options.courseId.value.forEach(id => {
                filters.where('id').filter(Operator.IsNotEqualTo, id);
            });
        }
    }

    if (options[FilterType.Topic]?.value.length) {
        filters.where('topic_id').filter(Operator.Have, options[FilterType.Topic]!.value.join('|'));
    }

    if (options[FilterType.Category]?.value.length) {
        filters.where('category_id').filter(Operator.Have, options[FilterType.Category]!.value.join('|'));
    }

    if (options[FilterType.Author]?.value.length) {
        filters.where('author_id').filter(Operator.Have, options[FilterType.Author]!.value.join('|'));
    }

    if (options[FilterType.CourseType]?.value.length) {
        filters.where('course_type').filter(Operator.IsEqualTo, options[FilterType.CourseType]!.value.join('|'));
    }

    if (options[FilterType.Skill]?.value.length) {
        filters.where('skill_ids').filter(Operator.Have, options[FilterType.Skill]!.value.join('|'));
    }

    return filters.build();
}

export function getAllCoursesNew(options: CourseListRequestOptions) {
    const filters = options.filters ? getCourseListFiltersParam(options.filters) : undefined;
    const pageNumber = options.page ?? 1;
    const pageSize = options.pageSize ?? 10;

    return adminOverviewApiClient
        .getListOfCourses(filters, undefined, pageNumber, pageSize)
        .then(extractAxiosData) as Promise<PaginatedDataUnmapped<IUnmappedAdminCourse>>;
}

export function createCourse({ courseType }: { courseType: CourseType }) {
    return builderCourseApiClient.postCourse({ course_type: courseType }).then(extractAxiosData);
}

export function editIframeElement(guid, data: PutIframeContentRequest) {
    return builderContentApiClient.putIframeContent(guid, data).then(extractAxiosData);
}

export function editVideoElement(guid, data: VideoContentElement) {
    return builderContentApiClient
        .putVideoContent(guid, { file_url: data.video_url, filename: data.filename })
        .then(extractAxiosData);
}
export function editQuizChoiceElement(guid, data: QuizChoiceQuestionContentBuilderContract) {
    if (!data.question) data.question = '';
    if (!data.description) data.description = '';

    if (data.choice_options) {
        data.choice_options = data.choice_options.filter(x => x.option !== '');

        if (!data.multiple_answers && data.choice_options.filter(x => x.is_correct).length > 1) return;
    }

    return builderContentApiClient.putQuizChoiceQuestionContent(guid, data).then(extractAxiosData);
}

export function editQuizRatingElement(guid: string, data: PutQuizRatingScaleRequest) {
    if (!data.question) {
        return;
    }
    return builderContentApiClient.putQuizRatingScaleContent(guid, data).then(extractAxiosData);
}

function _editChoiceListElement(guid, data: ChoiceQuestionContentBuilderContract) {
    if (!data.question) data.question = '';
    if (!data.description) data.description = '';

    if (data.choice_options) {
        data.choice_options = data.choice_options.filter(x => x.option !== '');

        if (!data.multiple_answers && data.choice_options.filter(x => x.is_correct).length > 1) return;
    }

    return builderContentApiClient.putChoiceQuestionContent(guid, data).then(extractAxiosData);
}

export function storeQuizAttempt(attempt: StoreQuizAttemptRequest) {
    return quizApiClient.storeQuizAttempt(attempt);
}
export function getQuizAttempts(attemptId: number) {
    return quizApiClient.getQuizAttempt(attemptId).then(extractAxiosData);
}

interface SubmitAttemptPayload {
    courseId: number;
    chapterId: number;
    pageId: number;
}

export function submitAttempt({ courseId, chapterId, pageId }: SubmitAttemptPayload) {
    return playerQuizApiClient.submitAttempt(courseId, chapterId, pageId).then(extractAxiosData);
}

interface CreateNewAttemptPayload {
    courseId: number;
    chapterId: number;
    pageId: number;
}

export function createNewAttempt({ courseId, chapterId, pageId }: CreateNewAttemptPayload) {
    return playerQuizApiClient.createNewAttempt(courseId, chapterId, pageId).then(extractAxiosData);
}

export const editChoiceListElement = debounce(_editChoiceListElement, 300);

export function editQuizOpenTextElement(guid: string, data: PutQuizOpenTextRequest) {
    if (!data.question) {
        return;
    }
    return builderContentApiClient.putQuizOpenTextContent(guid, data).then(extractAxiosData);
}
export function editAttachmentElement(guid, data: AttachmentContentElement) {
    return builderContentApiClient
        .putAttachmentContent(guid, {
            data: data.attachment_content_items!.map(item => {
                return {
                    ...item,
                    id: item.id!,
                    file_size: item.file_size!,
                    filename: item.file_name!,
                };
            }),
        })
        .then(extractAxiosData);
}

export function deleteCoursesBulk(coursesIds: number[]) {
    return builderCourseApiClient.deleteCourses({
        course_ids: coursesIds,
    });
}

export function archiveCoursesBulk(coursesIds: (number | undefined)[]) {
    if (coursesIds.includes(undefined)) {
        return;
    }
    return builderCourseApiClient.archiveCourses({ course_ids: coursesIds });
}

export function unArchiveCoursesBulk(coursesIds: (number | undefined)[]) {
    if (coursesIds.includes(undefined)) {
        return;
    }
    return builderCourseApiClient.unArchiveCourses({ course_ids: coursesIds });
}

interface SmartSelectionUser {
    first_name: string;
    id: number;
    image_url: string | null;
    job_title: string;
    last_name: string;
}

interface GetUsersQueryExclusiveSelection {
    excluded_user_ids: number[];
}
interface GetUsersQueryInclusiveSelection {
    included_user_ids: number[];
}

export type GetUsersQuerySmartSelection = GetUsersQueryInclusiveSelection | GetUsersQueryExclusiveSelection;

export function getUsersBySmartSelection({
    selection,
    page = 1,
    pageSize = 10,
    filters,
}: {
    selection: GetUsersQuerySmartSelection;
    page: number;
    pageSize: number;
    filters?: CoreEntitiesFilter;
}) {
    const q = new CoreQuery()
        .path([EntitiesList.LEARNING, EntitiesList.PERSONA, EntitiesList.USER])
        .filters(filters)
        .param({
            page,
            page_size: pageSize,
        });

    return httpClientCoreQuery.post<PaginatedDataUnmapped<SmartSelectionUser>>(q, selection).then(({ data }) => data);
}

export interface AddAssignmentQueryExclusiveSelection {
    mode: 'exclusive';
    queryArgs: {
        excluded_user_ids: number[];
    };
}
export interface AddAssignmentQueryInclusiveSelection {
    mode: 'inclusive';
    queryArgs: {
        user_ids: number[];
    };
}

export type AddAssignmentQuerySelection = AddAssignmentQueryInclusiveSelection | AddAssignmentQueryExclusiveSelection;

export function getAdminOverviewReport() {
    const q = new CoreQuery().path([
        EntitiesList.LEARNING,
        EntitiesList.PERSONA,
        EntitiesList.OVERVIEW,
        EntitiesList.REPORT,
    ]);
    return httpClient.get(q.query, { ...q.axiosConfigParams, responseType: 'blob' }).then(({ data }) => {
        return data;
    });
}
